import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRoom, ensureDefaultTimer } from '../services/dataClient';
import { getDefaultLiveConnectionsForPlan } from '../lib/planLimits';
import { withGuestWriteTokenInUrl } from '../lib/guestRoomToken';

export interface UseCreateGuestRoomResult {
  /** Создаёт гостевую комнату и переходит в неё. Возвращает roomId или null при ошибке. */
  openGuestRoom: () => Promise<string | null>;
  /** Текущее состояние создания комнаты. UI блокирует повторные клики по этому флагу. */
  creatingGuestRoom: boolean;
}

export interface UseCreateGuestRoomOptions {
  /** Внешний флаг занятости (например, `loading` из `useAuth`) — блокирует запуск. */
  disabled?: boolean;
}

/**
 * Создание гостевой комнаты для маркетинговых страниц.
 * Прячет работу с `services/dataClient` за UI-агностичный hook,
 * чтобы страницы не зависели от data-слоя напрямую.
 */
export function useCreateGuestRoom(
  options: UseCreateGuestRoomOptions = {},
): UseCreateGuestRoomResult {
  const { disabled = false } = options;
  const navigate = useNavigate();
  const [creatingGuestRoom, setCreatingGuestRoom] = useState(false);

  const openGuestRoom = useCallback(async (): Promise<string | null> => {
    if (disabled || creatingGuestRoom) return null;
    setCreatingGuestRoom(true);
    try {
      const roomId = await createRoom({
        name: `Быстрый старт ${new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}`,
        settings: { maxLiveConnections: getDefaultLiveConnectionsForPlan('free') },
        createdAt: Date.now(),
      });

      try {
        await ensureDefaultTimer(roomId);
      } catch (timerError) {
        console.error('Ошибка создания гостевого таймера:', timerError);
      }

      navigate(withGuestWriteTokenInUrl(`/room/${roomId}`, roomId));
      return roomId;
    } catch (error) {
      console.error('Ошибка создания гостевой комнаты:', error);
      alert('Не удалось создать комнату. Попробуйте снова.');
      return null;
    } finally {
      setCreatingGuestRoom(false);
    }
  }, [creatingGuestRoom, disabled, navigate]);

  return { openGuestRoom, creatingGuestRoom };
}
